import NextImage from 'next/image';
import { useTranslation } from 'next-i18next';
import { FC, useEffect, useRef } from 'react';

import GardenMap from '@/assets/images/garden-map.webp';
import { Button } from '@/components/shared/Button';
import { Icons, IconsTypes } from '@/components/shared/Icons';
import { ImageNoCaption } from '@/components/shared/Image';
import { Modal } from '@/components/shared/Modal';
import { useToggle } from '@/hooks/useToggle';
import { COMMON_LOCALE_PART } from '@/locale';
import { Colors, IPlantImage } from '@/types';
interface IPlantLocationMap {
  mainImage: IPlantImage;
  coords: {
    x: number;
    y: number;
  };
  plantName: string;
}

export const PlantMapLocation: FC<IPlantLocationMap> = ({ mainImage, coords, plantName }) => {
  const { x, y } = coords;

  const [isMapExpanded, toggleMapExpand] = useToggle();
  const { t } = useTranslation(COMMON_LOCALE_PART);

  const imageRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const image = imageRef.current as HTMLDivElement;
    const dot = dotRef.current as HTMLDivElement;
    const x1 = image?.offsetLeft + image?.clientWidth / 2;
    const y1 = image?.offsetTop + image?.clientHeight / 2;

    const x2 = dot?.offsetLeft + dot?.clientWidth / 10;
    const y2 = dot?.offsetTop + dot?.clientHeight / 10;

    const line: SVGLineElement = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('id', 'mySVG');
    line.setAttribute('x1', x1.toString());
    line.setAttribute('y1', y1.toString());
    line.setAttribute('x2', x2.toString());
    line.setAttribute('y2', y2.toString());
    line.setAttribute('stroke', '#247E80');
    line.setAttribute('stroke-width', '3px');
    line.setAttribute('stroke-linecap', 'round');

    svgRef.current?.appendChild(line);
  }, [isMapExpanded]);

  return (
    <>
      <div className="w-full rounded-lg bg-white p-6 shadow-lg md:p-11">
        <h2 className="text-md font-bold">{t('plant.info.map', { plantName })}</h2>

        <div className="relative mt-9 flex flex-col justify-between xl:flex-row">
          <div className="ml-auto md:ml-0">
            <Button variant={Colors.transparent} className="flex items-center !px-0" onClick={toggleMapExpand}>
              {Icons({ name: IconsTypes.EXPAND })}
              <span className="ml-3 font-bold text-black-800">{t('buttons.expand-map')}</span>
            </Button>
          </div>

          <div ref={containerRef} className="relative ml-auto md:max-w-lg">
            <svg ref={svgRef} className="absolute h-full w-full overflow-visible"></svg>
            <NextImage
              loading={'lazy'}
              className="ml-auto"
              src={GardenMap.src}
              width={GardenMap.width}
              height={GardenMap.height}
              alt="garden_image"
            />
            <div ref={dotRef} className={`point absolute h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full `} />

            {mainImage && (
              <div
                ref={imageRef}
                className="absolute -left-4 top-1/2 h-[82px] w-[82px] -translate-y-1/2 overflow-hidden rounded-full border-[4px] border-green md:-left-16  lg:-left-[400px] lg:h-[205px] lg:w-[205px] xl:h-[264px] xl:w-[264px]"
              >
                <div className='h-full w-full'>
                  <ImageNoCaption
                    className="h-full w-full object-cover"
                    src={mainImage.img}
                    optimized={mainImage.image}
                    alt={plantName}
                  />
                </div>
              </div>
            )}

            <style jsx>{`
              .point {
                top: ${y}%;
                left: ${x}%;
              }
            `}</style>
          </div>
        </div>
      </div>
      {isMapExpanded && (
        <Modal className="sm:w-full sm:max-w-[90vw] md:w-max" onClose={toggleMapExpand}>
          <div ref={containerRef} className="relative m-auto">
            <svg ref={svgRef} className="absolute h-full w-full overflow-visible"></svg>
            <NextImage
              className="max-h-[760px] p-2 md:max-w-[640px] lg:m-auto lg:max-h-[700px] lg:max-w-[700px]"
              src={GardenMap.src}
              width={GardenMap.width}
              height={GardenMap.height}
              alt="gargen_map"
            />
            <div ref={dotRef} className={`point absolute h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full `} />

            {mainImage && (
              <div
                ref={imageRef}
                className="absolute -left-0 top-12 h-[82px] w-[82px] -translate-y-1/2 overflow-hidden rounded-full border-[4px] border-green md:-left-0 md:top-24 md:h-[150px] md:w-[150px] lg:left-10 lg:top-28 lg:h-[205px] lg:w-[205px] "
              >
                <NextImage
                  src={mainImage.img}
                  alt={plantName}
                  height={82}
                  width={82}
                  className="h-full w-full object-cover"
                />
              </div>
            )}

            <style jsx>{`
              .point {
                top: ${y}%;
                left: ${x}%;
              }
            `}</style>
          </div>
        </Modal>
      )}
    </>
  );
};
